import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sun, Moon, ArrowLeft } from 'lucide-react';
import { THEMES, DEFAULT_LOGO_URL } from '../components/landing/landingConstants';
import { LandingNav } from '../components/landing/LandingNav';
import { LandingFooter } from '../components/landing/LandingFooter';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function About() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [aboutData, setAboutData] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('landingTheme') || 'light');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [contentRes, aboutRes] = await Promise.all([
          axios.get(`${API}/landing/content`),
          axios.get(`${API}/landing/about`),
        ]);
        setContent(contentRes.data);
        setAboutData(aboutRes.data);
      } catch (err) {
        console.error('Failed to load about page:', err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    localStorage.setItem('landingTheme', theme);
  }, [theme]);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  };

  const goToSignup = () => navigate('/onboarding');

  if (!content || !aboutData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
      </div>
    );
  }

  const isDark = theme === 'dark';
  const t = isDark ? THEMES.dark : THEMES.light;
  const logoUrl = content.branding?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${content.branding.logo_url}` : DEFAULT_LOGO_URL;
  const appName = content.branding?.app_name || 'Grassroots Coaching';

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} transition-colors`}>
      <LandingNav
        content={content} theme={theme} t={t} user={user}
        mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen}
        mobileMenuRef={mobileMenuRef} scrollToSection={scrollToSection}
        navigate={navigate} goToSignup={goToSignup} logoUrl={logoUrl} appName={appName}
      />

      {/* Theme toggle - fixed bottom left */}
      <button
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        className={`fixed bottom-6 left-6 z-50 p-3 rounded-full shadow-lg transition-all ${
          isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-[#007a33] text-white hover:bg-[#005c26]'
        }`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        {/* Back to main page */}
        <div className="mb-8">
          <button onClick={() => navigate('/')} className={`flex items-center gap-2 ${t.textSecondary} hover:opacity-80 transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Back to main page
          </button>
        </div>

        {/* Page Title */}
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">{aboutData.title || 'About Us'}</h1>
        {aboutData.subtitle && <p className={`text-lg ${t.textSecondary} mb-10`}>{aboutData.subtitle}</p>}
        {aboutData.body && <p className={`${t.textSecondary} mb-12 leading-relaxed whitespace-pre-wrap`}>{aboutData.body}</p>}

        {/* Feature Sections */}
        <div className="space-y-16">
          {(aboutData.sections || []).map((section, index) => (
            <div key={index} className={`flex flex-col ${index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 items-center`}>
              <div className="flex-1 space-y-4">
                <h2 className="text-2xl font-bold">{section.title}</h2>
                {section.subtitle && <p className={`${t.textSecondary} leading-relaxed whitespace-pre-wrap`}>{section.subtitle}</p>}
                {section.quote && (
                  <div className={`${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-[#007a33]/5 border-gray-200'} rounded-xl p-5 border`}>
                    <p className={`${t.textSecondary} italic`}>"{section.quote}"</p>
                    {section.quoteAuthor && <p className={`text-sm ${t.textMuted} mt-2`}>— {section.quoteAuthor}</p>}
                  </div>
                )}
              </div>
              {section.image && (
                <div className="flex-1 w-full">
                  <img
                    src={section.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${section.image}` : section.image}
                    alt={section.title}
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Back to main page */}
        <div className="mt-16 text-center">
          <button onClick={() => navigate('/')} className={`flex items-center gap-2 mx-auto ${t.textSecondary} hover:opacity-80 transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Back to main page
          </button>
        </div>
      </div>

      <LandingFooter content={content} logoUrl={logoUrl} appName={appName} />
    </div>
  );
}
